import {
  SmtpServer,
  WorkflowNotificationEmailNotificationData,
  WorkflowNotificationResource,
  WorkflowNotificationSmsNotificationData,
  WorkflowNotificationWhatsAppNotificationData,
  WorkflowNotificationType,
  WorkflowsService,
  OrganizationService,
} from '@/api';
import Breadcrumb from '@/components/breadcrumb';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { PanelsTopLeft, RefreshCw } from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { $org } from '@/stores/orgStore';
import EmailDetail from './components/EmailDetail';
import EmailList from './components/EmailList';
import SMSDetail from './components/SMSDetail';
import SMSList from './components/SMSList';
import TaskList from './components/TaskList';
import WhatsAppDetail from './components/WhatsAppDetail';
import WhatsAppList from './components/WhatsAppList';

export type UnifiedInboxBasePageProps = {
  organizationId: string;
};

export interface Email {
  id: string;
  threadId?: string | null;
  from: string;
  subject: string;
  date: string;
  body: string;
  read: boolean;
  smtpServer?: SmtpServer;
  messages?: Email[]; // if this email is actually a thread, all messages are stored here
  resources?: WorkflowNotificationResource[];
  nodeId?: string;
  workflowId?: string;
  organizationId?: string;
}
export interface SMS {
  id: string;
  threadId?: string | null;
  author: string;
  date: string;
  body: string;
  read: boolean;
  messages?: SMS[]; // if this email is actually a thread, all messages are stored here
  resources?: WorkflowNotificationResource[];
}

export interface WhatsAppMessage {
  id: string;
  threadId?: string | null;
  from: string;
  messageId: string;
  message: string;
  to: string;
  date: string;
  body: string;
  templateName: string;
  parameters: string[];
  messageTimestamp: string;
  read: boolean;
  messages?: WhatsAppMessage[];
  resources?: WorkflowNotificationResource[];
  phoneNumberId?: string;
  languageCode?: string;
}

const UnifiedInboxBasePage = ({
  organizationId,
}: UnifiedInboxBasePageProps) => {
  const org = useStore($org);
  const workflow = undefined;
  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name ? `/organization/${org?.id}/inbox` : '/organizations',
    },
    {
      label: 'Notifications and Tasks',
      href: `/organization/${org?.id}/inbox`,
    },
  ];

  const [emails, setEmails] = useState<Email[]>([]);
  const [smss, setSmss] = useState<SMS[]>([]);
  const [whatsapps, setWhatsapps] = useState<WhatsAppMessage[]>([]);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [selectedSmsId, setSelectedSmsId] = useState<string | null>(null);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState<string | null>(null);
  const [openEmailThread, setOpenEmailThread] = useState(false);
  const [openSMSThread, setOpenSMSThread] = useState(false);
  const [openWhatsAppThread, setOpenWhatsAppThread] = useState(false);

  const [notificationType, setNotificationType] =
    useState<WorkflowNotificationType>('EMAIL_NOTIFICATION');
  const [searchTerm, setSearchTerm] = useState('');
  const [openTasksPanel, setOpenTasksPanel] = useState(false);
  const toggleTasksPanel = () => setOpenTasksPanel(!openTasksPanel);

  const notifications = useQuery({
    queryKey: ['getNotifications', org, workflow, notificationType],
    queryFn: async () => {
      const res = await WorkflowsService.getWorkflowNotifications(
        org?.id,
        workflow,
        notificationType,
      );

      if (notificationType === 'SMS_NOTIFICATION') {
        // Map notifications into our Email interface, now including threadId.
        const smsFromNotifications: SMS[] = res
          .filter(n => n.data !== null)
          .map(notification => {
            const data =
              notification.data as WorkflowNotificationSmsNotificationData;
            return {
              id: notification.id, // backend GUID as string
              threadId: data.thread_id, // include thread_id for grouping
              author: data.author || '',
              // Use the createdAtUtc (or another timestamp) for the date display.
              date: new Date(notification.createdAtUtc).toISOString(),
              body: data.message || '',
              read: notification.readStatus === 'READ',
              resources: notification.workflowNotificationResources || [],
            };
          });

        // Group emails by threadId:
        // If an email has a threadId and another email with the same threadId exists,
        // add it to that thread's messages array.
        const groupedSmss: SMS[] = [];
        const threadMap = new Map<string, SMS>();
        smsFromNotifications.forEach(sms => {
          if (sms.threadId) {
            if (threadMap.has(sms.threadId)) {
              // Add this email to the messages array for the thread.
              const representative = threadMap.get(sms.threadId)!;
              if (!representative.messages) {
                representative.messages = [representative];
              }
              representative.messages.push(sms);
              // (Optional) Update the representative with the most recent email's info.
              if (new Date(sms.date) > new Date(representative.date)) {
                representative.id = sms.id;
                representative.author = sms.author;
                representative.date = sms.date;
                representative.body = sms.body;
                representative.read = sms.read;
              }
            } else {
              threadMap.set(sms.threadId, sms);
              groupedSmss.push(sms);
            }
          } else {
            // No threadId: add email as is.
            groupedSmss.push(sms);
          }
        });

        setSmss(groupedSmss);
      }

      console.log('rsssss', res);

      if (notificationType === 'EMAIL_NOTIFICATION') {
        // Map notifications into our Email interface, now including threadId.
        const emailsFromNotifications: Email[] = res
          .filter(n => n.data !== null)
          .map(notification => {
            const data =
              notification.data as WorkflowNotificationEmailNotificationData;
            return {
              id: notification.id, // backend GUID as string
              threadId: data.thread_id, // include thread_id for grouping
              from: data.from || '',
              subject: data.subject || '',
              smtpServer: data?.smtp_server,
              // Use the createdAtUtc (or another timestamp) for the date display.
              date: new Date(notification.createdAtUtc).toISOString(),
              body: data.message || '',
              read: notification.readStatus === 'READ',
              resources: notification.workflowNotificationResources || [],
              nodeId: notification.workflowNodeId || '',
              workflowId: notification.workflowId || '',
              organizationId: notification.organizationId || '',
            };
          });

        // Group emails by threadId:
        // If an email has a threadId and another email with the same threadId exists,
        // add it to that thread's messages array.
        const groupedEmails: Email[] = [];
        const threadMap = new Map<string, Email>();
        emailsFromNotifications.forEach(email => {
          if (email.threadId) {
            if (threadMap.has(email.threadId)) {
              // Add this email to the messages array for the thread.
              const representative = threadMap.get(email.threadId)!;
              if (!representative.messages) {
                representative.messages = [representative];
              }
              representative.messages.push(email);
              // (Optional) Update the representative with the most recent email's info.
              if (new Date(email.date) > new Date(representative.date)) {
                representative.id = email.id;
                representative.from = email.from;
                representative.subject = email.subject;
                representative.date = email.date;
                representative.body = email.body;
                representative.read = email.read;
              }
            } else {
              threadMap.set(email.threadId, email);
              groupedEmails.push(email);
            }
          } else {
            // No threadId: add email as is.
            groupedEmails.push(email);
          }
        });

        setEmails(groupedEmails);
      }
      if (notificationType === 'WHATSAPP_NOTIFICATION') {
        const whatsappsFromNotifications: WhatsAppMessage[] = res.filter(n => n.data !== null).map(notification => {
          const data = notification.data as WorkflowNotificationWhatsAppNotificationData;
          return {
            id: notification.id,
            to: data.to,
            from: data.from,
            messageId: data.message_id,
            threadId: data.thread_id,
            message: data.message,
            date: new Date(notification.createdAtUtc).toISOString(),
            body: data.message || '',
            templateName: data.template_name || '',
            parameters: data.parameters || [],
            messageTimestamp: data.message_timestamp,
            read: notification.readStatus === 'READ',
            resources: notification.workflowNotificationResources || [],
            phoneNumberId: data.phone_number_id,
            languageCode: data.language_code,
          };
        });

        // Group WhatsApp messages by threadId:
        // If a message has a threadId and another message with the same threadId exists,
        // add it to that thread's messages array.
        const groupedWhatsapps: WhatsAppMessage[] = [];
        const threadMap = new Map<string, WhatsAppMessage>();
        whatsappsFromNotifications.forEach(whatsapp => {
          if (whatsapp.threadId) {
            if (threadMap.has(whatsapp.threadId)) {
              // Add this message to the messages array for the thread.
              const representative = threadMap.get(whatsapp.threadId)!;
              if (!representative.messages) {
                representative.messages = [representative];
              }
              representative.messages.push(whatsapp);
              // Update the representative with the most recent message's info.
              if (new Date(whatsapp.messageTimestamp) > new Date(representative.messageTimestamp)) {
                representative.id = whatsapp.id;
                representative.from = whatsapp.from;
                representative.to = whatsapp.to;
                representative.message = whatsapp.message;
                representative.templateName = whatsapp.templateName;
                representative.messageTimestamp = whatsapp.messageTimestamp;
                representative.read = whatsapp.read;
              }
            } else {
              threadMap.set(whatsapp.threadId, whatsapp);
              groupedWhatsapps.push(whatsapp);
            }
          } else {
            // No threadId: add message as is.
            groupedWhatsapps.push(whatsapp);
          }
        });

        setWhatsapps(groupedWhatsapps);
      }

      return res;
    },
    refetchInterval: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: orgPhoneNumbers } = useQuery({
    queryKey: ['orgPhoneNumbers', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      if (!organizationId) return [];
      return await OrganizationService.getOrganizationPhoneNumbers(organizationId);
    },
  });

  const filteredEmails = useMemo(() => {
    if (notificationType !== 'EMAIL_NOTIFICATION') {
      // For demonstration, "others" returns no notifications.
      return [];
    }
    let filtered = emails;
    if (searchTerm) {
      filtered = filtered.filter(
        e =>
          e.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.body.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    return filtered;
  }, [notificationType, searchTerm, emails]);

  // Find the selected email (or thread representative) based on its id.
  const selectedEmail = useMemo(() => {
    return emails.find(e => e.id === selectedEmailId) || null;
  }, [selectedEmailId, emails]);

  const filteredSmss = useMemo(() => {
    if (notificationType !== 'SMS_NOTIFICATION') {
      // For demonstration, "others" returns no notifications.
      return [];
    }
    let filtered = smss;
    if (searchTerm) {
      filtered = filtered.filter(
        e =>
          e.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.body.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    return filtered;
  }, [notificationType, searchTerm, smss]);

  const selectedSms = useMemo(() => {
    return smss.find(e => e.id === selectedSmsId) || null;
  }, [selectedSmsId, smss]);

  const filteredWhatsapps = useMemo(() => {
    if (notificationType !== 'WHATSAPP_NOTIFICATION') {
      return [];
    }
    let filtered = whatsapps;
    if (searchTerm) {
      filtered = filtered.filter(
        w => w.to.toLowerCase().includes(searchTerm.toLowerCase()) ||
             w.templateName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [notificationType, searchTerm, whatsapps]);

  const selectedWhatsapp = useMemo(() => {
    return whatsapps.find(w => w.id === selectedWhatsappId) || null;
  }, [selectedWhatsappId, whatsapps]);

  return (
    <>
      <div className="flex h-full w-full flex-col gap-2 overflow-y-auto">
        <h3 className="m-0 flex-1 p-0">
          <Breadcrumb pages={pages} />
        </h3>

        {/* Page Header with actions */}
        <div className=" mt-2 flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">{`Smart Inbox`}</h2>
            <p className="text-muted-foreground">
              {`Track and manage all communications for ${org?.name}.`}
            </p>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            placeholder="Search messages ..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none"
          />
          <Button
            variant="outline"
            onClick={() => notifications.refetch()}
            disabled={notifications.isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 ${notifications.isFetching && 'animate-spin'}`}
            />
          </Button>

          <Button variant="outline" onClick={toggleTasksPanel} disabled>
            <PanelsTopLeft className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-0 flex gap-2">
          {openTasksPanel && (
            <div className="mt-2 w-[30%]">
              <TaskList />
            </div>
          )}

          <div className={`${openTasksPanel ? 'w-[70%]' : 'w-[100%]'} `}>
            <div className="mt-2">
              {/* Tabs */}
              <Tabs
                value={notificationType}
                onValueChange={val =>
                  setNotificationType(val as WorkflowNotificationType)
                }
              >
                <TabsList className="mb-3 space-x-2">
                  <TabsTrigger value="EMAIL_NOTIFICATION">Email</TabsTrigger>
                  <TabsTrigger value="SMS_NOTIFICATION">SMS</TabsTrigger>
                  <TabsTrigger value="WHATSAPP_NOTIFICATION">WhatsApp</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {notificationType == 'EMAIL_NOTIFICATION' && (
              <>
                <EmailList
                  emails={filteredEmails}
                  searchTerm={searchTerm}
                  onSearchTermChange={setSearchTerm}
                  onSelectEmail={setSelectedEmailId}
                  selectedEmailId={selectedEmailId}
                  onOpenEmailThreadDialog={setOpenEmailThread}
                />
                <EmailDetail
                  email={selectedEmail}
                  open={openEmailThread}
                  onOpenChange={setOpenEmailThread}
                />
              </>
            )}

            {notificationType == 'SMS_NOTIFICATION' && (
              <>
                <SMSList
                  smss={filteredSmss}
                  searchTerm={searchTerm}
                  onSearchTermChange={setSearchTerm}
                  onSelectSms={setSelectedSmsId}
                  selectedSmsId={selectedSmsId}
                  onOpenSMSThreadDialog={setOpenSMSThread}
                />
                <SMSDetail
                  sms={selectedSms}
                  onOpenChange={setOpenSMSThread}
                  open={openSMSThread}
                />
              </>
            )}

            {notificationType == 'WHATSAPP_NOTIFICATION' && (
            <>
              <WhatsAppList 
               messages={filteredWhatsapps}
               searchTerm={searchTerm} 
               onSearchTermChange={setSearchTerm}
               onSelectMessage={setSelectedWhatsappId}
               selectedMessageId={selectedWhatsappId}
               onOpenWhatsAppThreadDialog={setOpenWhatsAppThread}
               orgPhoneNumbers={orgPhoneNumbers || []}
              />
              <WhatsAppDetail
                message={selectedWhatsapp} 
                open={openWhatsAppThread} 
                onOpenChange={setOpenWhatsAppThread}
                 />
            </>
          )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UnifiedInboxBasePage;
